import type * as ents from '@attain-apps/entities'

import type * as mosaic from '../../mosaic'

import type { Definition } from './definition'

export type Values = Record<
  ents.core.dms.data.query.Variable.Name,
  ents.core.dms.data.Value | undefined
>
export namespace Values {
  /**
   * Context of the variable values lookup holder. Need to satisfy
   * {@link Definition.Holder} type since the type is also used as
   * {@link Definition.Holder} in some places where definitions list is needed
   * based on this holder context
   */
  export type Holder = Satisfies<
    ExactlyOneOf<{
      session: Holder.ForSessionVariables
      scenario: Holder.ForScenarioVariables
      page: Holder.ForPageVariables
      widget: Holder.ForWidgetVariables
    }>,
    Definition.Holder
  >
  export namespace Holder {
    /**
     * Used to encode context for variable values holder
     */
    export function stringify({
      session,
      scenario,
      page,
      widget,
    }: Holder): string {
      if (session) {
        return ForSessionVariables.stringify(session)
      }
      if (scenario) {
        return ForScenarioVariables.stringify(scenario)
      }
      if (page) {
        return ForPageVariables.stringify(page)
      }
      if (widget) {
        return ForWidgetVariables.stringify(widget)
      }
      return 'unknown'
    }

    export type ForSessionVariables = Definition.Holder.ForSessionVariables
    export namespace ForSessionVariables {
      /**
       * Returns encoded context for session variables
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({ appId }: ForSessionVariables): string {
        return `session_${appId}`
      }
    }

    export interface ForScenarioVariables
      extends Definition.Holder.ForScenarioVariables {
      scenarioId: ents.core.admin.Scenario.Id
    }
    export namespace ForScenarioVariables {
      /**
       * Returns encoded context for scenario variables
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({
        appId,
        scenarioId,
      }: ForScenarioVariables): string {
        return `scenario_${scenarioId}_app_${appId}`
      }
    }

    export interface ForPageVariables
      extends Definition.Holder.ForPageVariables {
      appId: ents.core.admin.App.Id
      scenarioId?: ents.core.admin.Scenario.Id
    }
    export namespace ForPageVariables {
      /**
       * Returns encoded context for page variables
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({
        pageId,
        appId,
        scenarioId,
      }: ForPageVariables): string {
        const context = `page_${pageId}_app_${appId}`
        return scenarioId ? `${context}_scenario_${scenarioId}` : context
      }
    }

    export interface ForWidgetVariables
      extends Definition.Holder.ForWidgetVariables {
      appId: ents.core.admin.App.Id
      scenarioId?: ents.core.admin.Scenario.Id
      pageId: ents.core.mosaic.Page.Id
      /**
       * Unique identifier to distinguish between multiple instances of the same
       * widget usually within a page layout
       */
      refId: mosaic.models.widget.Ref.Id
    }
    export namespace ForWidgetVariables {
      /**
       * Returns encoded context for widget variables
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({
        widgetId,
        appId,
        scenarioId,
        pageId,
        refId,
      }: ForWidgetVariables): string {
        let context = `widget_${widgetId}`
        context += `_ref_${refId}`
        context += `_app_${appId}_page_${pageId}`
        if (scenarioId) {
          context += `_scenario_${scenarioId}`
        }
        return context
      }
    }
  }

  /**
   * Lookup of stringified variables holder and its variable values
   */
  export type Lookup = Partial<Record<string, Values>>

  /**
   * Data for update variable value
   */
  export interface UpdateRequest {
    holder: Holder
    variableId: ents.core.dms.data.query.Variable.Name
    value: ents.core.dms.data.Value
  }

  /**
   * Represents a request that will write variables values to the state for a
   * given {@link Holder}
   */
  export interface PutRequest {
    holder: Holder
    values: Values
  }

  /**
   * Represents a request that will add variables values to the state for a
   * given {@link Holder}
   */
  export interface AddRequest {
    holder: Holder
    values: Values
  }
}
